import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { stripeConnectService } from '../../../services/stripeConnectService';

const DirectDepositSettings = () => {
  const { user } = useAuth();
  const [stripeAccount, setStripeAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStripeAccountStatus();
  }, []);

  const loadStripeAccountStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const status = await stripeConnectService?.getConnectStatus();
      if (status?.connected) {
        setStripeAccount(status?.account);
      }
    } catch (err) {
      console.error('Failed to load Stripe account:', err);
      setError(err?.message || 'Failed to load account information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      const result = await stripeConnectService?.createConnectAccount(user?.email);
      if (result?.onboardingUrl) {
        window.location.href = result?.onboardingUrl;
      }
    } catch (err) {
      console.error('Failed to connect Stripe:', err);
      
      // Check if it's a configuration error
      let errorMessage = err?.message || 'Failed to connect bank account';
      if (errorMessage?.includes('STRIPE_SECRET_KEY') || errorMessage?.includes('not configured')) {
        errorMessage = 'Stripe is not configured properly. Please contact support to enable bank account connections.';
      }
      
      setError(errorMessage);
      setIsProcessing(false);
    }
  };

  const handleCompleteOnboarding = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      const onboardingUrl = await stripeConnectService?.getOnboardingLink();
      if (onboardingUrl) {
        window.location.href = onboardingUrl;
      }
    } catch (err) {
      console.error('Failed to get onboarding link:', err);
      
      // Check if it's a configuration error
      let errorMessage = err?.message || 'Failed to continue onboarding';
      if (errorMessage?.includes('STRIPE_SECRET_KEY') || errorMessage?.includes('not configured')) {
        errorMessage = 'Stripe is not configured properly. Please contact support to enable bank account connections.';
      }
      
      setError(errorMessage);
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 flex items-start space-x-3">
          <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <p className="font-body text-sm text-error">{error}</p>
          </div>
        </div>
      )}

      {/* Bank Account Connection */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-heading text-2xl font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Building2" size={24} strokeWidth={2} className="text-primary" />
            <span>Bank Account</span>
          </h2>
          {stripeAccount?.onboardingCompleted && (
            <span className="px-3 py-1 bg-success/10 text-success text-sm font-medium rounded-full flex items-center space-x-1">
              <Icon name="ShieldCheck" size={16} strokeWidth={2} />
              <span>Connected</span>
            </span>
          )}
        </div>

        {!stripeAccount ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Building2" size={32} className="text-primary" strokeWidth={2} />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              Connect Your Bank Account
            </h3>
            <p className="font-body text-muted-foreground mb-6 max-w-md mx-auto">
              Link your bank account to receive payouts from your trailer rentals. Draggin Trailers takes 15% of every transaction as a platform fee.
            </p>
            <Button 
              variant="default" 
              size="lg" 
              iconName="Plus" 
              onClick={handleConnectStripe}
              disabled={isProcessing}
            >
              {isProcessing ? 'Connecting...' : 'Connect Bank Account'}
            </Button>
          </div>
        ) : !stripeAccount?.onboardingCompleted ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertCircle" size={32} className="text-warning" strokeWidth={2} />
            </div>
            <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
              Complete Your Setup
            </h3>
            <p className="font-body text-muted-foreground mb-6 max-w-md mx-auto">
              Your bank account connection is incomplete. Please complete the onboarding process to start receiving payouts.
            </p>
            <Button 
              variant="default" 
              size="lg" 
              onClick={handleCompleteOnboarding}
              disabled={isProcessing}
            >
              {isProcessing ? 'Loading...' : 'Complete Setup'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="border border-border rounded-lg p-4">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="Building2" size={24} className="text-primary" strokeWidth={2} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="font-body font-semibold text-foreground">
                      {stripeAccount?.bankName || 'Bank Account'}
                    </h3>
                    <span className="font-caption text-xs text-muted-foreground">
                      ••••{stripeAccount?.bankAccountLast4 || '0000'}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={stripeAccount?.chargesEnabled ? 'CheckCircle' : 'XCircle'} 
                        size={16} 
                        className={stripeAccount?.chargesEnabled ? 'text-success' : 'text-error'} 
                        strokeWidth={2} 
                      />
                      <span className="font-body text-sm text-muted-foreground">
                        Charges: {stripeAccount?.chargesEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={stripeAccount?.payoutsEnabled ? 'CheckCircle' : 'XCircle'} 
                        size={16} 
                        className={stripeAccount?.payoutsEnabled ? 'text-success' : 'text-error'} 
                        strokeWidth={2} 
                      />
                      <span className="font-body text-sm text-muted-foreground">
                        Payouts: {stripeAccount?.payoutsEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon 
                        name={stripeAccount?.w9VerificationCompleted ? 'CheckCircle' : stripeAccount?.w9SubmissionStatus === 'pending' ? 'AlertCircle' : 'Clock'} 
                        size={16} 
                        className={stripeAccount?.w9VerificationCompleted ? 'text-success' : stripeAccount?.w9SubmissionStatus === 'pending' ? 'text-warning' : 'text-muted-foreground'} 
                        strokeWidth={2} 
                      />
                      <span className="font-body text-sm text-muted-foreground">
                        W-9 Tax Form: {stripeAccount?.w9VerificationCompleted ? 'Verified' : stripeAccount?.w9SubmissionStatus === 'submitted' ? 'Under Review' : 'Pending'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Platform Fee Information */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-1" strokeWidth={2} />
          <div>
            <h3 className="font-body font-semibold text-foreground mb-2">Platform Fee Structure</h3>
            <ul className="space-y-2 font-body text-sm text-muted-foreground">
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={14} className="text-accent mt-1 flex-shrink-0" strokeWidth={2} />
                <span>Draggin Trailers charges a 15% platform fee on all rental transactions</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={14} className="text-accent mt-1 flex-shrink-0" strokeWidth={2} />
                <span>You receive 85% of the rental amount after the platform fee is deducted</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={14} className="text-accent mt-1 flex-shrink-0" strokeWidth={2} />
                <span>Payouts are processed automatically to your connected bank account</span>
              </li>
              <li className="flex items-start space-x-2">
                <Icon name="Check" size={14} className="text-accent mt-1 flex-shrink-0" strokeWidth={2} />
                <span>Bank account verification typically takes 1-2 business days</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-card rounded-lg shadow-elevation-2 p-6">
        <h2 className="font-heading text-xl font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Shield" size={20} strokeWidth={2} className="text-success" />
          <span>Security Settings</span>
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Lock" size={20} className="text-muted-foreground mt-1" strokeWidth={2} />
              <div>
                <h3 className="font-body font-medium text-foreground mb-1">Two-Factor Authentication</h3>
                <p className="font-body text-sm text-muted-foreground">Require verification code for account changes</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 border border-border rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Bell" size={20} className="text-muted-foreground mt-1" strokeWidth={2} />
              <div>
                <h3 className="font-body font-medium text-foreground mb-1">Transaction Notifications</h3>
                <p className="font-body text-sm text-muted-foreground">Get notified for all payout transactions</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectDepositSettings;